class RichEditorHelper {
  static normalizeHtmlForCompare(html) {
    if (!html) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const IGNORE_ATTRS = new Set(["dir", "id", "class", "contenteditable", "spellcheck", "draggable"]);
    const IGNORE_STYLE_PROPS = new Set(["white-space", "font-size", "line-height", "letter-spacing"]);
    const KEEP_STYLE_PROPS = new Set(["color", "background-color"]);

    const significantChildren = (el) =>
      Array.from(el.childNodes).filter((n) => !(n.nodeType === Node.TEXT_NODE && !n.nodeValue.trim()));

    const normalizeColor = (value) => {
      if (!value) return value;
      const v = value.trim().toLowerCase();
      let m = v.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([0-9.]+))?\s*\)$/i);
      if (m) {
        const r = Math.min(255, parseInt(m[1], 10));
        const g = Math.min(255, parseInt(m[2], 10));
        const b = Math.min(255, parseInt(m[3], 10));
        const a = m[4] !== undefined ? parseFloat(m[4]) : 1;
        return a >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${a})`;
      }
      m = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
      if (m) {
        let hex = m[1];
        if (hex.length === 3)
          hex = hex
            .split("")
            .map((ch) => ch + ch)
            .join("");
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgb(${r}, ${g}, ${b})`;
      }
      return v;
    };

    const replaceTag = (el, newTag) => {
      if (el.tagName === newTag.toUpperCase()) return el;
      const newEl = doc.createElement(newTag);
      for (const attr of Array.from(el.attributes)) newEl.setAttribute(attr.name, attr.value);
      while (el.firstChild) newEl.appendChild(el.firstChild);
      el.parentNode.replaceChild(newEl, el);
      return newEl;
    };

    const unwrap = (el) => {
      const parent = el.parentNode;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    };

    const walk = (node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      let el = node;

      if (el.tagName === "B") el = replaceTag(el, "strong");
      if (el.tagName === "I") el = replaceTag(el, "em");

      for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (name.startsWith("data-") || IGNORE_ATTRS.has(name)) el.removeAttribute(attr.name);
      }

      if (el.hasAttribute("style")) {
        const rules = el
          .getAttribute("style")
          .split(";")
          .map((s) => s.trim())
          .filter(Boolean)
          .map((rule) => {
            const [rawProp, ...rest] = rule.split(":");
            return [(rawProp || "").trim().toLowerCase(), (rest.join(":") || "").trim()];
          });

        const kept = [];
        for (const [prop, val] of rules) {
          if (IGNORE_STYLE_PROPS.has(prop) || !KEEP_STYLE_PROPS.has(prop)) continue;
          kept.push(`${prop}: ${normalizeColor(val)}`);
        }
        if (kept.length) el.setAttribute("style", kept.join("; "));
        else el.removeAttribute("style");
      }

      // A) Unwrap same-tag children with no attrs: <strong><strong>t</strong></strong> -> <strong>t</strong>
      for (const child of Array.from(el.children)) {
        if (child.tagName === el.tagName && child.attributes.length === 0) unwrap(child);
      }

      // B) Remove empty spans
      if (el.tagName === "SPAN" && el.attributes.length === 0) {
        unwrap(el);
        return; // children already moved up
      }

      for (const child of Array.from(el.children)) walk(child);
    };

    walk(doc.body);

    // BIS) Collapse outer==inner when outer has NO attrs and its only significant child is same tag.
    // e.g. <strong><strong style="color:red">x</strong></strong> => <strong style="color:red">x</strong>
    const collapseOuterEqualsInner = () => {
      let changed = false;
      const candidates = doc.querySelectorAll("strong, em, u, s");
      candidates.forEach((el) => {
        if (el.attributes.length !== 0) return;
        const kids = significantChildren(el);
        if (kids.length === 1 && kids[0].nodeType === Node.ELEMENT_NODE) {
          const only = kids[0];
          if (only.tagName === el.tagName) {
            el.parentNode.replaceChild(only, el);
            changed = true;
          }
        }
      });
      return changed;
    };
    while (collapseOuterEqualsInner()) {} // répéter jusqu'à stabilisation

    return doc.body.innerHTML
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .replace(/\s*(<\/(strong|em|u|s|span|p|br)>)\s*/g, "$1")
      .trim();
  }
}

export default RichEditorHelper;
